// Calendar export helpers — turn a programmazione's expiry date (scadenza)
// into something the user can drop into their device calendar. We support two
// universal, login-free paths:
//   1. an .ics (iCalendar) file — opens natively in Apple Calendar, Outlook,
//      and any desktop/mobile calendar app;
//   2. a Google Calendar "template" URL — one click adds the event to Google
//      Calendar in the browser.
// No backend, no OAuth: everything is generated client-side from the date.

import dayjs from "dayjs";

export interface CalendarEvent {
  title: string;
  /** ISO date/string for the day the event lands on (all-day). */
  date: string;
  description?: string;
  /** Optional deep link back into the app for the event body. */
  url?: string;
}

/** iCalendar text escaping: commas, semicolons, backslashes and newlines. */
const escapeICS = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

/** All-day events use DATE values (YYYYMMDD), DTEND being the exclusive next day. */
const toDateValue = (d: dayjs.Dayjs): string => d.format("YYYYMMDD");

/** Compact UTC timestamp form for DTSTAMP. Built from the native Date so we
 *  don't depend on the dayjs UTC plugin being registered. */
const toStampValue = (d: dayjs.Dayjs): string => {
  const iso = d.toDate().toISOString(); // e.g. 2026-07-03T10:20:30.000Z
  return iso.replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
};

/**
 * Build a valid iCalendar (.ics) document for a single all-day event with a
 * one-day-before popup reminder. Lines are CRLF-terminated per RFC 5545.
 */
export function buildICS(event: CalendarEvent): string {
  const start = dayjs(event.date).startOf("day");
  const end = start.add(1, "day");
  const stamp = dayjs();
  // UID must be stable-ish and unique; derive from the date + a slug of title.
  const uid = `${toDateValue(start)}-${event.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}@coachingmaster.net`;

  const descriptionParts = [event.description, event.url].filter(Boolean) as string[];

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Coaching Master//Programmazioni//IT",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toStampValue(stamp)}`,
    `DTSTART;VALUE=DATE:${toDateValue(start)}`,
    `DTEND;VALUE=DATE:${toDateValue(end)}`,
    `SUMMARY:${escapeICS(event.title)}`,
    descriptionParts.length
      ? `DESCRIPTION:${escapeICS(descriptionParts.join("\n"))}`
      : "",
    "BEGIN:VALARM",
    "TRIGGER:-P1D",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeICS(event.title)}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  return lines.join("\r\n");
}

/** Trigger a browser download of the .ics file for the given event. */
export function downloadICS(event: CalendarEvent): void {
  const ics = buildICS(event);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Build a Google Calendar "create event" template URL for an all-day event.
 * `dates` uses the YYYYMMDD/YYYYMMDD form (end exclusive) for all-day events.
 */
export function buildGoogleCalendarUrl(event: CalendarEvent): string {
  const start = dayjs(event.date).startOf("day");
  const end = start.add(1, "day");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${toDateValue(start)}/${toDateValue(end)}`,
  });
  const details = [event.description, event.url].filter(Boolean).join("\n");
  if (details) params.set("details", details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
